import { Link } from 'react-router';

import type { StorefrontProductSummary } from '@/features/storefront/api/storefront';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { ProductCard } from '@/features/storefront/components/ProductCard';
import { RAIL_ITEM, Rail } from '@/features/storefront/components/Rail';

/**
 * How wide a card's slot is once the row has divided itself up. The widest
 * slot is a fifth of a 72rem column, near enough 220px; a phone shows two of
 * them across a small viewport, so under 45vw.
 */
const RAIL_IMAGE_SIZES = '(min-width: 1024px) 240px, 45vw';

/**
 * A row of products a shopper can push sideways through.
 *
 * A rail rather than a grid because these are a suggestion, not the catalog:
 * they sit under something the shopper came for, and a grid of twelve would
 * push the page's own content out of the way. Empty renders nothing at all -
 * a heading over no products reads as a broken shop.
 *
 * The card is the framed tile the catalog listing wears, so a product looks
 * the same wherever a shopper meets it.
 */
export function ProductRail({
  title,
  products,
}: {
  title: string;
  products: StorefrontProductSummary[];
}) {
  const paths = useStorefrontPaths();
  if (products.length === 0) return null;

  return (
    <section className="mt-14">
      <Rail
        label={title}
        itemCount={products.length}
        heading={<h2 className="text-sm font-semibold uppercase tracking-wide">{title}</h2>}
      >
        {products.map((product) => (
          <li key={product.id} className={RAIL_ITEM}>
            <Link to={paths.product(product.slug)} className="group block h-full">
              <ProductCard product={product} presentation="framed" sizes={RAIL_IMAGE_SIZES} />
            </Link>
          </li>
        ))}
      </Rail>
    </section>
  );
}
